import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Map,
  Square,
  Users,
} from 'lucide-react';
import './App.css';

// Import authentication
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';

// Import components
import Dashboard from './components/Dashboard';
import DepartmentList from './components/DepartmentList';
import DepartmentDetails from './components/DepartmentDetails';
import AreaList from './components/AreaList';
import AreaDetails from './components/AreaDetails';
import PeopleList from './components/PeopleList';
import MetricsList from './components/MetricsList';
import MetricsDetails from './components/MetricsDetails';
import MetricValuesList from './components/MetricValuesList';
import DashboardList from './components/DashboardList';
import DashboardDetails from './components/DashboardDetails';
import apiService from './services/api';

// Navigation component with active state and collapsible sidebar
function Navigation({ isCollapsed, toggleCollapse }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dashboards, setDashboards] = useState([]);
  const [dashboardsExpanded, setDashboardsExpanded] = useState(true); // Default expanded
  const [dashboardsLoading, setDashboardsLoading] = useState(true);
  
  useEffect(() => {
    const fetchDashboards = async () => {
      try {
        setDashboardsLoading(true);
        const response = await apiService.dashboards.getAll();
        // Dashboard model doesn't have is_active field, so show all dashboards
        setDashboards(response.data || []);
      } catch (err) {
        console.error('Error fetching dashboards:', err);
        // Set empty array on error so UI doesn't break
        setDashboards([]);
      } finally {
        setDashboardsLoading(false);
      }
    };
    
    fetchDashboards();
  }, []);
  
  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/departments', label: 'Departments', icon: Building2 },
    { path: '/areas', label: 'Areas', icon: Map },
    { path: '/people', label: 'People', icon: Users },
    { path: '/metrics', label: 'Metrics', icon: BarChart3 },
    { path: '/metric-values', label: 'Metric Values', icon: ListChecks },
  ];
  
  const isDashboardsActive = location.pathname === '/dashboards' || location.pathname.startsWith('/dashboards/');

  return (
    <nav className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && (
          <div className="nav-brand">
            <div className="logo-container">
              <span className="logo-text">kaptio</span>
              <div className="logo-dot"></div>
            </div>
            <h2>Metric Mgmt.</h2>
          </div>
        )}
        <button 
          className="sidebar-toggle" 
          onClick={toggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? '→' : '←'}
        </button>
      </div>
      <div className="nav-links">
        {/* Dashboard link */}
        {navItems.filter(item => item.path === '/').map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive ? 'active' : ''}`}
              title={isCollapsed ? item.label : ''}
            >
              <span className="nav-icon"><Icon size={16} strokeWidth={2} /></span>
              {!isCollapsed && <span className="nav-label">{item.label}</span>}
            </Link>
          );
        })}
        
        {/* Dashboards with collapsible tree */}
        {!isCollapsed ? (
          <div className="nav-group">
            <div 
              className="nav-link nav-group-header"
              onClick={() => setDashboardsExpanded(!dashboardsExpanded)}
              style={{ cursor: 'pointer' }}
            >
              <span className="nav-icon"><LayoutGrid size={16} strokeWidth={2} /></span>
              <span className="nav-label" style={{ flex: 1 }}>Dashboards</span>
              <span className="nav-icon">
                {dashboardsExpanded ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
              </span>
            </div>
            {dashboardsExpanded && (
              <div className="nav-group-children">
                {dashboardsLoading ? (
                  <div className="nav-link nav-child" style={{ paddingLeft: '3rem', opacity: 0.7 }}>
                    <span className="nav-label">Loading...</span>
                  </div>
                ) : dashboards.length === 0 ? (
                  <div className="nav-link nav-child" style={{ paddingLeft: '3rem', opacity: 0.7 }}>
                    <span className="nav-label">No dashboards</span>
                  </div>
                ) : (
                  dashboards.map((dashboard) => {
                    const isActive = location.pathname === `/dashboards/${dashboard.id}`;
                    return (
                      <Link
                        key={dashboard.id}
                        to={`/dashboards/${dashboard.id}`}
                        className={`nav-link nav-child ${isActive ? 'active' : ''}`}
                        style={{ paddingLeft: '3rem' }}
                      >
                        <span className="nav-icon"><Square size={12} fill="currentColor" strokeWidth={0} /></span>
                        <span className="nav-label">{dashboard.name}</span>
                      </Link>
                    );
                  })
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="nav-link" title="Dashboards">
            <span className="nav-icon"><LayoutGrid size={16} strokeWidth={2} /></span>
          </div>
        )}
        
        {/* Other nav items */}
        {navItems.filter(item => item.path !== '/').map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive ? 'active' : ''}`}
              title={isCollapsed ? item.label : ''}
            >
              <span className="nav-icon"><Icon size={16} strokeWidth={2} /></span>
              {!isCollapsed && <span className="nav-label">{item.label}</span>}
            </Link>
          );
        })}
      </div>
      {!isCollapsed && user && (
        <div className="sidebar-footer">
          <div className="sidebar-footer-name">
            {user.name || user.username}
          </div>
          <button
            onClick={logout}
            className="sidebar-footer-logout"
          >
            Logout
          </button>
        </div>
      )}
    </nav>
  );
}

function AppContent() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { authenticated, loading } = useAuth();

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!authenticated) {
    return <Login />;
  }

  return (
    <div className={`App ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Navigation isCollapsed={isSidebarCollapsed} toggleCollapse={toggleSidebar} />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboards" element={<ProtectedRoute><DashboardList /></ProtectedRoute>} />
          <Route path="/dashboards/:id" element={<ProtectedRoute><DashboardDetails /></ProtectedRoute>} />
          <Route path="/departments" element={<ProtectedRoute><DepartmentList /></ProtectedRoute>} />
          <Route path="/departments/:id" element={<ProtectedRoute><DepartmentDetails /></ProtectedRoute>} />
          <Route path="/areas" element={<ProtectedRoute><AreaList /></ProtectedRoute>} />
          <Route path="/areas/:id" element={<ProtectedRoute><AreaDetails /></ProtectedRoute>} />
          <Route path="/people" element={<ProtectedRoute><PeopleList /></ProtectedRoute>} />
          <Route path="/metrics" element={<ProtectedRoute><MetricsList /></ProtectedRoute>} />
          <Route path="/metrics/:id" element={<ProtectedRoute><MetricsDetails /></ProtectedRoute>} />
          <Route path="/metric-values" element={<ProtectedRoute><MetricValuesList /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;